/**
 * Roll arrays for the battle simulator (`BattleSimulatorPage`). The pure battle
 * engines (`resolveBattle` / `resolveBattleSequential`) take all randomness as a
 * caller-supplied `number[]` of 0..1 values, so the simulator generates one here
 * and passes the SAME array to both models — the only variable between them is
 * then the model itself, not the dice.
 *
 * With a `seed` the sequence is deterministic (mulberry32), which is a dev-only
 * affordance for reproducing a specific fight; without one it uses `Math.random`.
 */

/** Small, fast, well-distributed seeded PRNG. Returns a function yielding 0..1. */
function mulberry32(seed: number): () => number {
    let a = seed >>> 0;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/**
 * Build `count` rolls. `seed` (a finite number) makes it reproducible; anything
 * else falls back to `Math.random`. `count` should comfortably exceed what a
 * battle consumes — the engines treat an exhausted array as "no more triggers",
 * so over-provisioning is harmless.
 */
export function makeRolls(count: number, seed?: number): number[] {
    const next = seed !== undefined && Number.isFinite(seed) ? mulberry32(seed) : Math.random;
    return Array.from({ length: count }, () => next());
}
